import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import type { PetInput } from "@/lib/validations/pet";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { quotedIlikePattern } from "@/lib/utils/postgrest-filters";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { generateUUID } from "@/lib/utils/uuid";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { getActivePetWeights, sortPetWeightsDesc } from "@/lib/utils/pet-weight";
import { useTranslation } from "react-i18next";
import {
  reconcilePetDaycareHotelDraft,
  EMPTY_PET_DAYCARE_HOTEL_DRAFT,
  type PetDaycareHotelDraft,
} from "@/components/pets/daycare-hotel/petDaycareHotelDraft";
import { savePetDaycareHotelDraft } from "@/components/pets/daycare-hotel/savePetDaycareHotelDraft";
import { petDaycareHotelDraftKey } from "@/components/pets/daycare-hotel/usePetDaycareHotelDraft";
import { petReservationSummaryKey } from "@/pages/daycare-hotel/reservation/usePetReservationSummary";

type OwnerPhoneRow = Database["public"]["Tables"]["owners_phones"]["Row"];
type OwnerEmailRow = Database["public"]["Tables"]["owners_emails"]["Row"];
type CityRow = Database["public"]["Tables"]["cities"]["Row"];
type StateRow = Database["public"]["Tables"]["states"]["Row"];

type OwnerPhoneSummary = Pick<OwnerPhoneRow, "phone_number" | "is_primary" | "is_whatsapp">;

type OwnerEmailSummary = Pick<OwnerEmailRow, "email" | "is_primary">;

type PetAttachmentRow = Database["public"]["Tables"]["pet_attachments"]["Row"];
type OwnerAddressSummary = Database["public"]["Tables"]["owners_addresses"]["Row"] & {
  city?: (CityRow & { state?: StateRow | null }) | null;
};
type OwnerCustomerSummary = {
  customer_id: string | null;
  deleted_at: string | null;
};
type PetWeightRow = Database["public"]["Tables"]["pet_weights"]["Row"];
type PetConditionRow = Database["public"]["Tables"]["pet_conditions"]["Row"] & {
  condition?: { id: string; name: string; type: string } | null;
};
type PetAllergyRow = Database["public"]["Tables"]["pet_conditions"]["Row"] & {
  condition?: { id: string; name: string; type: string } | null;
};
type PetTemperamentRow = Database["public"]["Tables"]["pet_conditions"]["Row"] & {
  condition?: { id: string; name: string; type: string } | null;
};

type PetsCustomersRow = Database["public"]["Tables"]["pets_customers"]["Row"];

export type PetSummary = {
  id: string;
  name: string;
  birth_date: string | null;
  notes: string | null;
  photo?: { file_path: string } | { file_path: string }[] | null;
  species?: { name: string } | null;
  breed?: { name: string } | null;
  allergies: Array<{ id: string; name: string }>;
  temperaments: Array<{ id: string; name: string }>;
};

type PetCustomerRecord = { notes: string | null };
type PetCustomerRelation = PetCustomerRecord | PetCustomerRecord[] | null | undefined;

type PetOwnerWithContacts = {
  id: string;
  name: string;
  phones: OwnerPhoneSummary[];
  emails: OwnerEmailSummary[];
  document?: string | null;
  addresses?: OwnerAddressSummary[];
  pets?: unknown;
} & Record<string, unknown>;

type PetListQueryRow = PetWithOwner & {
  pet_customer?: PetCustomerRelation;
};

type PetDetailQueryRow = PetWithOwner &
  Record<string, unknown> & {
    attachments?: PetAttachmentRow[] | null;
    pet_customer?: PetCustomerRelation;
    weights?: PetWeightRow[] | null;
    pet_conditions_data?: PetConditionRow[] | null;
    temperaments?: PetTemperamentRow[] | null;
  };

interface PetAttachmentWithUrl extends PetAttachmentRow {
  storage_path: string | null;
  signed_url: string | null;
}

const extractPetCustomerNotes = (petCustomer: PetCustomerRelation): string | null => {
  if (!petCustomer) return null;
  const link = Array.isArray(petCustomer) ? petCustomer[0] : petCustomer;
  return link?.notes ?? null;
};

const ensureArray = <T>(value: unknown): T[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => Boolean(item));
};

const uniqueById = <T extends { id: string | number }>(items: T[]): T[] => {
  const seen = new Set<string | number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  // Tratar erros do Supabase que podem vir como objetos
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return "Erro inesperado";
};

const normalizeRecordNumber = (value?: string | null): string | null => {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
};

const fetchCurrentCustomerId = (): Promise<string> => getCurrentClinicCustomerId();

export const useNextPetRecordNumber = () => {
  return useQuery({
    queryKey: ["pets", "next-record-number"],
    queryFn: async () => {
      // Use suggest instead of reserve to avoid incrementing counter before saving
      const { data, error } = await supabase.rpc("suggest_pet_record_number");
      if (error) throw error;
      return data ?? null;
    },
    staleTime: Infinity,
    gcTime: 1000 * 60 * 10,
  });
};

export interface Pet {
  id: string;
  owner_id: string;
  name: string;
  record_number: string | null;
  gender_id: string | null;
  sterilization_id: string | null;
  birth_date: string | null;
  species_id: string | null;
  breed_id: string | null;
  size_category_id: string | null;
  coat_type_id: string | null;
  color_id: string | null;
  status_id: string;
  death_date: string | null;
  death_cause: string | null;
  microchip_number: string | null;
  health_plan: string | null;
  pedigree_number: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface PetWithOwner extends Pet {
  owner: PetOwnerWithContacts;
  photo?: { file_path: string } | { file_path: string }[] | null;
  species?: { name: string } | null;
  breed?: { name: string } | null;
  gender?: { name: string } | null;
  status?: { name: string } | null;
  sterilization?: { name: string } | null;
  coat_type?: { name: string } | null;
  color?: { name: string } | null;
  city?: { name: string } | null;
  state?: { name: string } | null;
}

// Listar pets com owner
export const usePets = (searchTerm?: string) => {
  return useQuery({
    queryKey: ["pets", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("pets")
        .select(`
          *,
          owner:owners!inner(
            id, name,
            phones:owners_phones(phone_number, is_primary, is_whatsapp),
            emails:owners_emails(email, is_primary)
          ),
          photo:pet_photos(file_path),
          species(name),
          breed:breeds(name),
          gender:pet_genders(name),
          pet_customer:pets_customers(notes)
        `)
        .is("deleted_at", null);

      // Busca por nome do pet, nome do owner, telefone, email, documento
      if (searchTerm) {
        const p = quotedIlikePattern(searchTerm);
        query = query.or(`name.ilike.${p},owner.name.ilike.${p}`);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []).map((item) => {
        const { pet_customer, ...rest } = item as PetListQueryRow;
        return {
          ...rest,
          notes: extractPetCustomerNotes(pet_customer),
        };
      });
    },
  });
};

export const usePetsByOwner = (ownerId?: string) => {
  return useQuery({
    queryKey: ["pets", "owner", ownerId],
    enabled: Boolean(ownerId),
    queryFn: async () => {
      if (!ownerId) return [];

      const { data, error } = await supabase
        .from("pets")
        .select(
          `
            *,
            owner:owners!inner(
              id,
              name,
              phones:owners_phones(phone_number, is_primary, is_whatsapp),
              emails:owners_emails(email, is_primary)
            ),
            photo:pet_photos(file_path),
            species(name),
            breed:breeds(name),
            gender:pet_genders(name),
            pet_customer:pets_customers(notes)
          `,
        )
        .eq("owner_id", ownerId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data ?? []).map((item) => {
        const { pet_customer, ...rest } = item as PetListQueryRow;
        return {
          ...rest,
          notes: extractPetCustomerNotes(pet_customer),
        };
      });
    },
  });
};

// Resumo leve do pet (para sidebars/modais): alergias, temperamento e notas
export const usePetSummary = (id?: string) => {
  const { data: currentCustomerId } = useCurrentClinicCustomerId();

  return useQuery<PetSummary | null>({
    queryKey: ["pet-summary", currentCustomerId, id],
    enabled: Boolean(id) && !!currentCustomerId,
    queryFn: async () => {
      if (!id || !currentCustomerId) return null;

      const { data, error } = await supabase
        .from("pets")
        .select(
          `
          id,
          name,
          birth_date,
          photo:pet_photos(file_path),
          species:species(name),
          breed:breeds(name),
          pet_customer:pets_customers(customer_id, notes, deleted_at),
          pet_conditions:pet_conditions(condition:conditions(id, name, type))
        `,
        )
        .eq("id", id)
        .is("deleted_at", null)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const record = data as unknown as {
        id: string;
        name: string;
        birth_date: string | null;
        photo?: { file_path: string } | { file_path: string }[] | null;
        species?: { name: string } | null;
        breed?: { name: string } | null;
        pet_customer?: PetsCustomersRow[] | PetsCustomersRow | null;
        pet_conditions?: Array<{ condition?: { id: string; name: string; type: string } | null }> | null;
      };

      const petCustomerRows = ensureArray<PetsCustomersRow>(record.pet_customer);
      const activePetCustomerRow =
        petCustomerRows.find(
          (row) => row.deleted_at === null && row.customer_id === currentCustomerId!
        ) ??
        petCustomerRows.find((row) => row.deleted_at === null) ??
        null;

      const notes = activePetCustomerRow?.notes ?? null;

      const petConditions = ensureArray<{ condition?: { id: string; name: string; type: string } | null }>(
        record.pet_conditions,
      );

      const allergies = uniqueById(
        petConditions
          .filter((item) => item.condition?.type === "allergy")
          .map((item) => ({ id: item.condition?.id ?? "", name: item.condition?.name ?? "" }))
          .filter((item) => Boolean(item.id && item.name)),
      );

      const temperaments = uniqueById(
        petConditions
          .filter((item) => item.condition?.type === "temperament")
          .map((item) => ({ id: item.condition?.id ?? "", name: item.condition?.name ?? "" }))
          .filter((item) => Boolean(item.id && item.name)),
      );

      return {
        id: record.id,
        name: record.name,
        birth_date: record.birth_date,
        notes,
        photo: record.photo ?? null,
        species: record.species ?? null,
        breed: record.breed ?? null,
        allergies,
        temperaments,
      };
    },
  });
};

// Obter detalhes de um pet
export const usePet = (id: string) => {
  return useQuery({
    queryKey: ["pet", id],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const query = (supabase as any)
        .from("pets")
        .select(`
          *,
          owner:owners(
            id, name, document,
            owner_customer:owners_customers(customer_id, deleted_at),
            phones:owners_phones(*),
            emails:owners_emails(*),
            addresses:owners_addresses(*, city:cities(id, name, state_id, state:states(id, name, code)))
          ),
          photo:pet_photos(*),
          attachments:pet_attachments(*),
          weights:pet_weights(*),
          pet_conditions_data:pet_conditions(condition:conditions(*)),
          species(name),
          breed:breeds(name),
          gender:pet_genders(name),
          sterilization:pet_sterilizations(name),
          coat_type:coat_types(name),
          color:colors(name),
          status:pet_statuses(name),
          size_category:pet_size_categories(name),
          pet_customer:pets_customers(notes)
        `)
        .eq("id", id)
        .is("deleted_at", null)
        .maybeSingle();

      const result = await query;
      const { data, error } = result;

      if (error) throw error;

      if (!data) return null;

      const typedData = data as unknown as PetDetailQueryRow;
      const { pet_customer, attachments, ...rest } = typedData;

      const rawAttachments = Array.isArray(attachments) ? attachments : [];
      const activeAttachments = rawAttachments.filter((attachment) => !attachment.deleted_at);

      const attachmentsWithUrls = await Promise.all(
        activeAttachments.map(async (attachment): Promise<PetAttachmentWithUrl> => {
          const storagePathCandidate =
            normalizeStoragePath(attachment.file_path, attachment.storage_bucket) ??
            normalizeStoragePath(attachment.filename, attachment.storage_bucket) ??
            attachment.filename ??
            null;

          let signedUrl: string | null = null;

          if (storagePathCandidate && !storagePathCandidate.startsWith("http")) {
            const { data: signedData, error: signedError } = await supabase.storage
              .from("pet-attachments")
              .createSignedUrl(storagePathCandidate, 60 * 60);

            if (!signedError && signedData?.signedUrl) {
              signedUrl = signedData.signedUrl;
            }
          }

          if (
            !signedUrl &&
            typeof attachment.file_path === "string" &&
            attachment.file_path.startsWith("http")
          ) {
            signedUrl = attachment.file_path;
          }

          return {
            ...attachment,
            storage_path: storagePathCandidate,
            signed_url: signedUrl,
          };
        })
      );

      const ownerPhonesRaw = ensureArray<OwnerPhoneRow>(rest.owner?.phones);
      const ownerEmailsRaw = ensureArray<OwnerEmailRow>(rest.owner?.emails);
      const ownerAddressesRaw = ensureArray<OwnerAddressSummary>(rest.owner?.addresses);
      const ownerCustomerRows = ensureArray<OwnerCustomerSummary>(rest.owner?.owner_customer);

      const ownerPhones: OwnerPhoneSummary[] = ownerPhonesRaw
        .map(({ phone_number, is_primary, is_whatsapp }) => ({
          phone_number,
          is_primary: Boolean(is_primary),
          is_whatsapp: is_whatsapp ?? null,
        }))
        .filter((phone) => Boolean(phone.phone_number));

      const ownerEmails: OwnerEmailSummary[] = ownerEmailsRaw
        .map(({ email, is_primary }) => ({
          email,
          is_primary: Boolean(is_primary),
        }))
        .filter((email) => Boolean(email.email));

      const currentCustomerId = await fetchCurrentCustomerId();
      const activeOwnerCustomerRow =
        ownerCustomerRows.find(
          (row) => row.deleted_at === null && row.customer_id === currentCustomerId,
        ) ??
        ownerCustomerRows.find((row) => row.deleted_at === null) ??
        null;
      const ownerCustomerNotes = null;

      const weightsRaw = ensureArray<PetWeightRow>(rest.weights);
      const sortedWeights = sortPetWeightsDesc(getActivePetWeights(weightsRaw));

      const petConditionsDataRaw = ensureArray<PetConditionRow>(rest.pet_conditions_data);

      const conditionsRaw = uniqueById(
        petConditionsDataRaw
          .filter((item) => item.condition?.type === "condition")
          .map((item) => ({
            id: item.condition?.id ?? "",
            condition: item.condition
              ? { id: item.condition.id, name: item.condition.name, type: item.condition.type }
              : null,
          })),
      );

      const allergiesRaw = uniqueById(
        petConditionsDataRaw
          .filter((item) => item.condition?.type === "allergy")
          .map((item) => ({
            id: item.condition?.id ?? "",
            condition: item.condition
              ? { id: item.condition.id, name: item.condition.name, type: item.condition.type }
              : null,
          })),
      );

      const temperamentsRaw = uniqueById(
        petConditionsDataRaw
          .filter((item) => item.condition?.type === "temperament")
          .map((item) => ({
            id: item.condition?.id ?? "",
            condition: item.condition
              ? { id: item.condition.id, name: item.condition.name, type: item.condition.type }
              : null,
          })),
      );

      return {
        ...rest,
        owner: rest.owner
          ? {
            ...rest.owner,
            phones: ownerPhones,
            emails: ownerEmails,
            addresses: ownerAddressesRaw,
            notes: ownerCustomerNotes,
          }
          : rest.owner,
        attachments: attachmentsWithUrls,
        weights: sortedWeights,
        conditions: conditionsRaw,
        allergies: allergiesRaw,
        temperaments: temperamentsRaw,
        notes: extractPetCustomerNotes(pet_customer),
      };
    },
    enabled: !!id,
  });
};

// Criar pet
export const useCreatePet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");
  const { t: tDaycareHotel } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: async (input: PetInput & { suppressToast?: boolean }): Promise<Pet | undefined> => {
      const customerId = await fetchCurrentCustomerId();
      const petId = generateUUID();
      const recordNumber = normalizeRecordNumber(input.record_number);

      const normalizedNotes =
        input.notes && input.notes.trim().length > 0 ? input.notes.trim() : null;
      const normalizedHealthPlan =
        input.health_plan && input.health_plan.trim().length > 0 ? input.health_plan.trim() : null;

      // 1. Criar pet (sem retorno para evitar SELECT pós-insert)
      const { error: petError } = await supabase
        .from("pets")
        .insert(
          {
            id: petId,
            owner_id: input.owner_id,
            name: input.name,
            record_number: recordNumber,
            gender_id: input.gender_id || null,
            sterilization_id: input.sterilization_id || null,
            birth_date: input.birth_date || null,
            species_id: input.species_id || null,
            breed_id: input.breed_id || null,
            size_category_id: input.size_category_id || null,
            coat_type_id: input.coat_type_id || null,
            color_id: input.color_id || null,
            status_id: input.status_id,
            death_date: input.death_date || null,
            death_cause: input.death_cause || null,
            microchip_number: input.microchip_number || null,
            health_plan: normalizedHealthPlan,
            pedigree_number: input.pedigree_number || null,
            daycare_hotel_notes: input.daycare_hotel_notes?.trim() || null,
          } as never,
        );

      if (petError) throw petError;

      const daycareHotelDraft = (input.daycare_hotel as PetDaycareHotelDraft | undefined) ?? null;
      if (daycareHotelDraft) {
        const { failed } = await savePetDaycareHotelDraft(
          petId,
          reconcilePetDaycareHotelDraft(EMPTY_PET_DAYCARE_HOTEL_DRAFT, daycareHotelDraft),
        );
        if (failed > 0) {
          toast({ title: tDaycareHotel("petForm.partialSaveError"), variant: "destructive" });
        }
      }

      const { error: petCustomerError } = await supabase
        .from("pets_customers")
        .upsert(
          {
            pet_id: petId,
            customer_id: customerId,
            notes: normalizedNotes,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "pet_id,customer_id" }
        );

      if (petCustomerError) throw petCustomerError;

      // 2. Adicionar peso inicial (se fornecido)
      if (input.current_weight) {
        const { error: weightError } = await supabase
          .from("pet_weights")
          .insert({
            pet_id: petId,
            weight: input.current_weight,
            source: "registration",
          });

        if (weightError) throw weightError;
      }

      // 3. Adicionar alergias
      if (input.allergies && input.allergies.length > 0) {
        const allergiesData = input.allergies.map((condition_id) => ({
          pet_id: petId,
          condition_id,
        }));

        const { error: allergiesError } = await supabase
          .from("pet_conditions")
          .insert(allergiesData);

        if (allergiesError) throw allergiesError;
      }

      // 4. Adicionar temperamentos
      if (input.temperaments && input.temperaments.length > 0) {
        const temperamentsData = input.temperaments.map((condition_id) => ({
          pet_id: petId,
          condition_id,
        }));

        const { error: temperamentsError } = await supabase
          .from("pet_conditions")
          .insert(temperamentsData);

        if (temperamentsError) throw temperamentsError;
      }

      return {
        id: petId,
        owner_id: input.owner_id,
        name: input.name,
        record_number: recordNumber,
        gender_id: input.gender_id || null,
        sterilization_id: input.sterilization_id || null,
        birth_date: input.birth_date || null,
        species_id: input.species_id || null,
        breed_id: input.breed_id || null,
        size_category_id: input.size_category_id || null,
        coat_type_id: input.coat_type_id || null,
        color_id: input.color_id || null,
        status_id: input.status_id,
        death_date: input.death_date || null,
        death_cause: input.death_cause || null,
        microchip_number: input.microchip_number || null,
        health_plan: normalizedHealthPlan,
        pedigree_number: input.pedigree_number || null,
        notes: normalizedNotes,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
      } as Pet;
    },
    onSuccess: (createdPet, variables) => {
      invalidatePatientsCache(queryClient, {
        ownerId: createdPet?.owner_id ?? null,
        petIds: createdPet?.id ? [createdPet.id] : null,
      });
      if (createdPet?.id) {
        queryClient.invalidateQueries({ queryKey: petReservationSummaryKey(createdPet.id) });
        queryClient.invalidateQueries({ queryKey: petDaycareHotelDraftKey(createdPet.id) });
        queryClient.invalidateQueries({ queryKey: ["stay-packs"] });
      }
      if (!variables.suppressToast) {
        toast({
          title: t("phase8.pets.createdTitle"),
        });
      }
    },
    onError: (error: unknown, variables) => {
      if (!variables.suppressToast) {
        toast({
          title: t("phase8.pets.createErrorTitle"),
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    },
  });
};

// Atualizar pet
export const useUpdatePet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");
  const { t: tDaycareHotel } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: PetInput }) => {
      const customerId = await fetchCurrentCustomerId();
      const recordNumber = normalizeRecordNumber(input.record_number);

      const normalizedNotes =
        input.notes && input.notes.trim().length > 0 ? input.notes.trim() : null;
      const normalizedHealthPlan =
        input.health_plan && input.health_plan.trim().length > 0 ? input.health_plan.trim() : null;

      // 1. Atualizar dados do pet
      const { error: petError } = await supabase
        .from("pets")
        .update({
          owner_id: input.owner_id,
          name: input.name,
          record_number: recordNumber,
          gender_id: input.gender_id || null,
          sterilization_id: input.sterilization_id || null,
          birth_date: input.birth_date || null,
          species_id: input.species_id || null,
          breed_id: input.breed_id || null,
          size_category_id: input.size_category_id || null,
          coat_type_id: input.coat_type_id || null,
          color_id: input.color_id || null,
          status_id: input.status_id,
          death_date: input.death_date || null,
          death_cause: input.death_cause || null,
          microchip_number: input.microchip_number || null,
          health_plan: normalizedHealthPlan,
          pedigree_number: input.pedigree_number || null,
          daycare_hotel_notes: input.daycare_hotel_notes?.trim() || null,
        } as never)
        .eq("id", id);

      if (petError) throw petError;

      const daycareHotelDraft = (input.daycare_hotel as PetDaycareHotelDraft | undefined) ?? null;
      const daycareHotelInitial =
        (input.daycare_hotel_initial as PetDaycareHotelDraft | undefined) ?? EMPTY_PET_DAYCARE_HOTEL_DRAFT;
      if (daycareHotelDraft) {
        const { failed } = await savePetDaycareHotelDraft(
          id,
          reconcilePetDaycareHotelDraft(daycareHotelInitial, daycareHotelDraft),
        );
        if (failed > 0) {
          toast({ title: tDaycareHotel("petForm.partialSaveError"), variant: "destructive" });
        }
      }

      const { error: petCustomerError } = await supabase
        .from("pets_customers")
        .upsert(
          {
            pet_id: id,
            customer_id: customerId,
            notes: normalizedNotes,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "pet_id,customer_id" }
        );

      if (petCustomerError) throw petCustomerError;

      // 2. Atualizar alergias
      // Deletar apenas alergias (type='allergy') deste pet
      const { data: existingAllergies } = await supabase
        .from("pet_conditions")
        .select("condition_id, condition:conditions(type)")
        .eq("pet_id", id);

      if (existingAllergies) {
        const allergyConditionIds = existingAllergies
          .filter((item: { condition?: { type?: string | null } | null; condition_id: string }) => item.condition?.type === 'allergy')
          .map((item: { condition?: { type?: string | null } | null; condition_id: string }) => item.condition_id);

        if (allergyConditionIds.length > 0) {
          await supabase
            .from("pet_conditions")
            .delete()
            .eq("pet_id", id)
            .in("condition_id", allergyConditionIds);
        }
      }

      if (input.allergies && input.allergies.length > 0) {
        const allergiesData = input.allergies.map((condition_id) => ({
          pet_id: id,
          condition_id,
        }));
        await supabase.from("pet_conditions").insert(allergiesData);
      }

      // 3. Atualizar temperamentos
      // Deletar apenas temperamentos (type='temperament') deste pet
      const { data: existingTemperaments } = await supabase
        .from("pet_conditions")
        .select("condition_id, condition:conditions(type)")
        .eq("pet_id", id);

      if (existingTemperaments) {
        const temperamentConditionIds = existingTemperaments
          .filter((item: { condition?: { type?: string | null } | null; condition_id: string }) => item.condition?.type === 'temperament')
          .map((item: { condition?: { type?: string | null } | null; condition_id: string }) => item.condition_id);

        if (temperamentConditionIds.length > 0) {
          await supabase
            .from("pet_conditions")
            .delete()
            .eq("pet_id", id)
            .in("condition_id", temperamentConditionIds);
        }
      }

      if (input.temperaments && input.temperaments.length > 0) {
        const temperamentsData = input.temperaments.map((condition_id) => ({
          pet_id: id,
          condition_id,
        }));
        await supabase.from("pet_conditions").insert(temperamentsData);
      }

      if (input.current_weight && input.current_weight > 0) {
        await supabase.from("pet_weights").insert({
          pet_id: id,
          weight: input.current_weight,
          source: "profile_update",
        });
      }

      return id;
    },
    onSuccess: (id) => {
      invalidatePatientsCache(queryClient);
      queryClient.invalidateQueries({ queryKey: ["pet", id] });
      queryClient.invalidateQueries({ queryKey: petReservationSummaryKey(id) });
      queryClient.invalidateQueries({ queryKey: petDaycareHotelDraftKey(id) });
      queryClient.invalidateQueries({ queryKey: ["stay-packs"] });
      toast({
        title: t("phase8.pets.updatedTitle"),
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("phase8.pets.updateErrorTitle"),
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });
};

// Deletar pet (soft delete)
export const useDeletePet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (id: string) => {
      // SECURITY DEFINER RPC: validates clinic access + patients:delete permission and cascades via deleted_at
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc("soft_delete_pet", {
        p_pet_id: id,
      });

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      invalidatePatientsCache(queryClient);
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment"] });
      queryClient.invalidateQueries({ queryKey: ["grooming-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["grooming-session"] });
      queryClient.invalidateQueries({ queryKey: ["package-purchases"] });
      toast({
        title: t("phase8.pets.deletedTitle"),
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("phase8.pets.deleteErrorTitle"),
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });
};

// Adicionar peso ao pet
export const useAddPetWeight = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async ({
      petId,
      weight,
      measuredAt,
      source = "manual",
    }: {
      petId: string;
      weight: number;
      measuredAt?: string | null;
      source?: "manual" | "registration" | "profile_update" | "consultation";
    }) => {
      const { error } = await supabase.from("pet_weights").insert({
        pet_id: petId,
        weight,
        measured_at: measuredAt ?? undefined,
        source,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pet", variables.petId] });
      toast({
        title: t("phase8.pets.weightAddedTitle"),
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("phase8.pets.weightAddErrorTitle"),
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });
};
